import React, { useMemo } from "react";
import { Box, Card, Typography } from "@mui/material";
import { LineChart } from "@mui/x-charts";
import moment from "moment";
import { T, SERIES, cardSx, chartChromeSx } from "utils/backrrTokens";
import InfoHint from "./InfoHint";

// Small multiples, not one shared axis and never a dual axis.
//
// Both series are daily counts, but their magnitudes differ by an order of
// magnitude — a 147-invitation spike against a startup peak of 5. On a shared
// axis the startup series flattens into the baseline and says nothing. A second
// y-axis is the textbook wrong answer: it manufactures correlation through
// independent scaling. Two panels, shared x, each scaled to its own series.
//
// The two colours come from startup-web's alternating CHART_COLORS. They are
// deliberately one-per-panel: validated as two series in a SINGLE chart they
// fail badly (CVD ΔE 1.9, normal-vision ΔE 9.4), so colour must never be what
// tells these two apart — each panel's own title does that.
const PANELS = [
  { key: "startups", label: "New Startups", color: SERIES[0] },
  { key: "invitations", label: "Invitations", color: SERIES[1] },
];

// Thirty date labels in a row overlap into an unreadable smear. Show about
// eight and let the tooltip carry the rest.
const MAX_TICKS = 8;

const Panel = ({ rows, panel, showAxis }) => {
  const values = rows.map((r) => r[panel.key] ?? 0);
  const peak = Math.max(...values, 0);
  const total = values.reduce((s, v) => s + v, 0);
  const gradientId = `area-${panel.key}`;
  const step = Math.max(1, Math.ceil(rows.length / MAX_TICKS));

  // Headroom above the peak. Left to itself the axis ends exactly ON the
  // highest value, so the peak's mark — a circle with a radius, not a point —
  // is drawn half outside the plotting area and clipped flat. One whole unit
  // minimum keeps a small series (peak 3) honest; 15% handles a large one.
  const ceiling = peak <= 0 ? 1 : Math.max(peak + 1, Math.ceil(peak * 1.15));

  return (
    <Box sx={{ marginTop: 1 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} paddingLeft="4px">
        <Box display="flex" alignItems="center" gap={0.75}>
          <Box
            component="span"
            sx={{ width: 8, height: 8, borderRadius: "2px", backgroundColor: panel.color, flexShrink: 0 }}
          />
          <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: T.ink }}>{panel.label}</Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={0.75}>
          {[
            { label: "total", value: total },
            { label: "peak", value: peak },
          ].map((stat) => (
            <Box
              key={stat.label}
              sx={{
                display: "inline-flex",
                gap: "4px",
                alignItems: "baseline",
                background: T.pill,
                borderRadius: "6px",
                padding: "1px 6px",
              }}
            >
              <Typography sx={{ fontSize: "0.72rem", color: T.inkSubtle }}>{stat.label}</Typography>
              <Typography
                sx={{ fontSize: "0.72rem", fontWeight: 600, color: T.inkMuted, fontVariantNumeric: "tabular-nums" }}
              >
                {stat.value}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true">
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={panel.color} stopOpacity={0.26} />
            <stop offset="100%" stopColor={panel.color} stopOpacity={0.02} />
          </linearGradient>
        </defs>
      </svg>

      <LineChart
        height={showAxis ? 118 : 98}
        series={[
          {
            data: values,
            label: panel.label,
            color: panel.color,
            area: true,
            // monotoneX, not natural. A natural spline overshoots its own data
            // between points: it arcs ABOVE a peak and dips BELOW zero between
            // two quiet days, drawing counts that never happened. monotoneX
            // stays within the values it is given.
            curve: "monotoneX",
            showMark: rows.length <= 14,
          },
        ]}
        xAxis={[
          {
            scaleType: "point",
            data: rows.map((r) => r.date),
            valueFormatter: (d) => moment(d, "YYYY-MM-DD").format("DD MMM"),
            // Only every Nth label is drawn, and only the bottom panel labels
            // at all — the top panel shares the same x.
            tickInterval: (value, index) => showAxis && index % step === 0,
          },
        ]}
        // Each panel scales to its own series. tickMinStep stops a quiet range
        // from rendering fractional counts like 0.5.
        yAxis={[{ min: 0, max: ceiling, tickMinStep: 1 }]}
        // right: 16 rather than 12 so a mark on the LAST day is not sliced by
        // the same edge the peak was.
        margin={{ top: 14, right: 16, bottom: showAxis ? 26 : 6, left: 40 }}
        grid={{ horizontal: true }}
        slotProps={{ legend: { hidden: true } }}
        sx={{ ...chartChromeSx, "& .MuiAreaElement-root": { fill: `url(#${gradientId})` } }}
      />
    </Box>
  );
};

const DailyActivityChart = ({ daily, isLoading }) => {
  const rows = useMemo(() => daily ?? [], [daily]);

  if (isLoading) return <Card sx={{ ...cardSx, height: "22rem" }} className="skeleton" />;

  const hasAny = rows.some((r) => r.startups > 0 || r.invitations > 0);

  return (
    <Card sx={{ ...cardSx, padding: "14px 16px 6px" }}>
      <Box display="flex" alignItems="center">
        <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: T.ink }}>Daily Activity</Typography>
        <InfoHint
          label="Daily Activity"
          text="Two panels, one shared date axis, each scaled to its OWN series. Invitations outnumber new startups by an order of magnitude, so a shared axis would flatten the startup line into the baseline. Compare shapes between panels, not heights."
        />
      </Box>
      <Typography sx={{ fontSize: "0.75rem", color: T.inkSubtle }}>
        Each panel is scaled to its own series, so a spike in one cannot flatten the other
      </Typography>

      {!hasAny ? (
        <Typography padding={6} textAlign="center" fontSize="0.8rem" color={T.inkSubtle}>
          No startups or invitations in this range.
        </Typography>
      ) : (
        PANELS.map((panel, i) => (
          <Panel key={panel.key} rows={rows} panel={panel} showAxis={i === PANELS.length - 1} />
        ))
      )}
    </Card>
  );
};

export default DailyActivityChart;
